import dayjs from "dayjs";
import { useState } from "react";

import { IAuditLogFilters } from "../../../../@types/AuditLog";
import { useAuditLogFilterOptions } from "../../../../hooks/queries/admin/auditLogs";
import {
  AdminDateField,
  AdminSelectField,
  CheckboxField,
  FilterGroup,
  FormGrid,
} from "../../shared";
import {
  AUDIT_FILTER_FIELD_HEIGHT,
  AuditFilterButton,
  AuditFilterInput,
  AuditFilterLabel,
  AuditFiltersActions,
  AuditFiltersCard,
  AuditFiltersFooter,
  AuditFiltersHint,
} from "../../shared/AuditLogPanel/AuditLog.styles";
import {
  auditWindowError,
  CHANGE_OPERATIONS,
  MAX_AUDIT_WINDOW_DAYS,
} from "../../shared/AuditLogPanel/auditLogWindow";

interface IAuditLogFiltersProps {
  onApplyFilters: (filters: Partial<IAuditLogFilters>) => void;
  isLoading: boolean;
}

const DATE_VALUE = "YYYY-MM-DD";
const DEFAULT_START_DATE = () => dayjs().subtract(6, "day").format(DATE_VALUE);
const DEFAULT_END_DATE = () => dayjs().format(DATE_VALUE);

/** `schema.table`: the table dropdown sets both filters at once. */
const tableKey = (schemaName: string, tableName: string) =>
  `${schemaName}.${tableName}`;

export function AuditLogFilters({
  onApplyFilters,
  isLoading,
}: IAuditLogFiltersProps) {
  const { filterOptions } = useAuditLogFilterOptions();

  const [table, setTable] = useState("");
  const [operation, setOperation] = useState("");
  // Views are logged on every open and bury the changes, so they start hidden.
  const [hideViews, setHideViews] = useState(true);
  const [source, setSource] = useState("");
  const [applicationUserEmail, setApplicationUserEmail] = useState("");
  const [applicationUserId, setApplicationUserId] = useState("");
  const [entityId, setEntityId] = useState("");
  const [startDate, setStartDate] = useState(DEFAULT_START_DATE());
  const [endDate, setEndDate] = useState(DEFAULT_END_DATE());

  const rangeError = auditWindowError({ startDate, endDate });

  const handleApply = () => {
    if (rangeError) return;

    const selectedTable = filterOptions?.tables.find(
      (t) => tableKey(t.schemaName, t.tableName) === table
    );
    const parsedUserId = applicationUserId
      ? Number(applicationUserId)
      : undefined;

    onApplyFilters({
      schemaName: selectedTable?.schemaName,
      tableName: selectedTable?.tableName,
      operation: operation || undefined,
      operations: !operation && hideViews ? CHANGE_OPERATIONS : undefined,
      source: source || undefined,
      applicationUserEmail: applicationUserEmail.trim() || undefined,
      applicationUserId:
        parsedUserId !== undefined && !Number.isNaN(parsedUserId)
          ? parsedUserId
          : undefined,
      entityId: entityId.trim() || undefined,
      startDate: dayjs(startDate).startOf("day").toISOString(),
      endDate: dayjs(endDate).endOf("day").toISOString(),
      page: 1,
    });
  };

  const handleReset = () => {
    const resetStart = DEFAULT_START_DATE();
    const resetEnd = DEFAULT_END_DATE();
    setTable("");
    setOperation("");
    setHideViews(true);
    setSource("");
    setApplicationUserEmail("");
    setApplicationUserId("");
    setEntityId("");
    setStartDate(resetStart);
    setEndDate(resetEnd);
    onApplyFilters({
      schemaName: undefined,
      tableName: undefined,
      operation: undefined,
      operations: CHANGE_OPERATIONS,
      source: undefined,
      applicationUserEmail: undefined,
      applicationUserId: undefined,
      entityId: undefined,
      startDate: dayjs(resetStart).startOf("day").toISOString(),
      endDate: dayjs(resetEnd).endOf("day").toISOString(),
      page: 1,
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleApply();
    }
  };

  return (
    <AuditFiltersCard>
      <FormGrid>
        <FilterGroup>
          <AuditFilterLabel htmlFor="audit-log-from">From</AuditFilterLabel>
          <AdminDateField
            id="audit-log-from"
            value={startDate}
            onChange={setStartDate}
          />
        </FilterGroup>

        <FilterGroup>
          <AuditFilterLabel htmlFor="audit-log-to">To</AuditFilterLabel>
          <AdminDateField
            id="audit-log-to"
            value={endDate}
            onChange={setEndDate}
          />
        </FilterGroup>

        <FilterGroup>
          <AuditFilterLabel htmlFor="audit-log-table">Table</AuditFilterLabel>
          <AdminSelectField
            id="audit-log-table"
            height={AUDIT_FILTER_FIELD_HEIGHT}
            value={table}
            onChange={(e) => setTable(e.target.value)}
          >
            <option value="">All tables</option>
            {filterOptions?.tables.map((t) => (
              <option
                key={tableKey(t.schemaName, t.tableName)}
                value={tableKey(t.schemaName, t.tableName)}
              >
                {t.tableName} ({t.schemaName})
              </option>
            ))}
          </AdminSelectField>
        </FilterGroup>

        <FilterGroup>
          <AuditFilterLabel htmlFor="audit-log-operation">
            Operation
          </AuditFilterLabel>
          <AdminSelectField
            id="audit-log-operation"
            height={AUDIT_FILTER_FIELD_HEIGHT}
            value={operation}
            onChange={(e) => setOperation(e.target.value)}
          >
            <option value="">All operations</option>
            {filterOptions?.operations.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </AdminSelectField>
        </FilterGroup>

        <FilterGroup>
          <AuditFilterLabel htmlFor="audit-log-source">Source</AuditFilterLabel>
          <AdminSelectField
            id="audit-log-source"
            height={AUDIT_FILTER_FIELD_HEIGHT}
            value={source}
            onChange={(e) => setSource(e.target.value)}
          >
            <option value="">All sources</option>
            {filterOptions?.sources.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </AdminSelectField>
        </FilterGroup>

        <FilterGroup>
          <AuditFilterLabel htmlFor="audit-log-user-email">
            User email
          </AuditFilterLabel>
          <AuditFilterInput
            id="audit-log-user-email"
            type="text"
            placeholder="Full or partial email"
            value={applicationUserEmail}
            onChange={(e) => setApplicationUserEmail(e.target.value)}
            onKeyDown={handleKeyDown}
          />
        </FilterGroup>

        <FilterGroup>
          <AuditFilterLabel htmlFor="audit-log-user-id">
            User ID
          </AuditFilterLabel>
          <AuditFilterInput
            id="audit-log-user-id"
            type="text"
            inputMode="numeric"
            placeholder="e.g. 1234"
            value={applicationUserId}
            onChange={(e) =>
              setApplicationUserId(e.target.value.replace(/\D/g, ""))
            }
            onKeyDown={handleKeyDown}
          />
        </FilterGroup>

        <FilterGroup>
          <AuditFilterLabel htmlFor="audit-log-entity-id">
            Record ID
          </AuditFilterLabel>
          <AuditFilterInput
            id="audit-log-entity-id"
            type="text"
            placeholder="ID of the changed record"
            value={entityId}
            onChange={(e) => setEntityId(e.target.value)}
            onKeyDown={handleKeyDown}
          />
        </FilterGroup>
      </FormGrid>

      <AuditFiltersFooter>
        <AuditFiltersHint error={Boolean(rangeError)}>
          {rangeError ??
            `Up to ${MAX_AUDIT_WINDOW_DAYS} days per search. For older history, move both dates back.`}
        </AuditFiltersHint>

        <AuditFiltersActions>
          <CheckboxField>
            <input
              type="checkbox"
              checked={hideViews}
              // A chosen operation already decides whether views show.
              disabled={Boolean(operation)}
              onChange={(e) => setHideViews(e.target.checked)}
            />
            Hide views
          </CheckboxField>
          <AuditFilterButton variant="secondary" onClick={handleReset}>
            Reset
          </AuditFilterButton>
          <AuditFilterButton
            variant="primary"
            onClick={handleApply}
            disabled={isLoading || Boolean(rangeError)}
          >
            Apply filters
          </AuditFilterButton>
        </AuditFiltersActions>
      </AuditFiltersFooter>
    </AuditFiltersCard>
  );
}
